/**
 * Formation layout — converts a FormationDef + headcount into slot offsets.
 * Shapes follow data/formations.json: `shape` is grid/line/column/wedge,
 * `spacing` is metres between slots, `depth_ratio` scales depth vs width.
 * This is the template for the UE formation-placement system.
 */
import type { FormationDef } from "../data/types";

export interface Slot {
  x: number; // right of facing direction, metres
  z: number; // behind the front rank, metres (positive = back)
}

/**
 * Compute slot offsets for `count` soldiers. Origin is the formation anchor
 * (front-centre); the caller rotates/translates by the formation's heading.
 */
export function computeFormationSlots(def: FormationDef, count: number): Slot[] {
  const slots: Slot[] = [];
  if (count <= 0) return slots;
  const s = def.spacing;
  const depthScale = def.depth_ratio;

  switch (def.shape) {
    case "line": {
      // Ranks of ~16; depth grows slowly.
      const rankWidth = 16;
      for (let i = 0; i < count; i++) {
        const rank = Math.floor(i / rankWidth);
        const file = i % rankWidth;
        const inRank = Math.min(rankWidth, count - rank * rankWidth);
        slots.push({
          x: (file - (inRank - 1) / 2) * s,
          z: rank * s * (0.5 + depthScale),
        });
      }
      break;
    }
    case "column": {
      const colDepth = 16;
      for (let i = 0; i < count; i++) {
        const file = Math.floor(i / colDepth);
        const rank = i % colDepth;
        const inFile = Math.min(colDepth, count - file * colDepth);
        slots.push({
          x: file * s * (0.5 + depthScale),
          z: (rank - (inFile - 1) / 2) * s,
        });
      }
      break;
    }
    case "wedge": {
      let placed = 0;
      let rank = 0;
      while (placed < count) {
        const width = rank * 2 + 1;
        for (let f = 0; f < width && placed < count; f++, placed++) {
          slots.push({ x: (f - rank) * s, z: rank * s * (0.5 + depthScale) });
        }
        rank++;
      }
      break;
    }
    case "grid":
    default: {
      // Near-square block; aspect adjusted by depth_ratio.
      const aspect = Math.sqrt(count / Math.max(depthScale, 0.05));
      const cols = Math.max(1, Math.round(aspect));
      for (let i = 0; i < count; i++) {
        const row = Math.floor(i / cols);
        const col = i % cols;
        const inRow = Math.min(cols, count - row * cols);
        slots.push({
          x: (col - (inRow - 1) / 2) * s,
          z: row * s,
        });
      }
      break;
    }
  }
  return slots;
}

/**
 * Facing-relative offset -> world position for a heading in radians. */
export function slotToWorld(
  slot: Slot,
  anchorX: number,
  anchorZ: number,
  heading: number,
): { x: number; z: number } {
  const cos = Math.cos(heading);
  const sin = Math.sin(heading);
  return {
    x: anchorX + slot.x * cos - slot.z * sin,
    z: anchorZ + slot.x * sin + slot.z * cos,
  };
}

/**
 * Road marching column: FOUR men abreast (owner correction 2026-09-21 —
 * not two columns). While a formation's anchor is on a road it narrows
 * from its battle layout into this 4-wide column so it fits the road;
 * off the road it resumes its normal layout. Anchor at front-centre,
 * files across (x), ranks back (z), same spacing as the battle layout.
 */
export const ROAD_COLUMN_WIDTH = 4;

export function computeRoadColumnSlots(
  count: number,
  spacing: number,
): Slot[] {
  const slots: Slot[] = [];
  if (count <= 0) return slots;
  const width = Math.min(ROAD_COLUMN_WIDTH, count);
  for (let i = 0; i < count; i++) {
    const rank = Math.floor(i / width);
    const file = i % width;
    const inRank = Math.min(width, count - rank * width);
    slots.push({
      x: (file - (inRank - 1) / 2) * spacing,
      z: rank * spacing,
    });
  }
  return slots;
}
